import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Typography, type TypographyProps } from '@heroui/react';
import { DashboardLanguageToggle } from '@/layout/components/DashboardLanguageToggle';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UseSetupPendingPageResult } from './useSetupPendingPage';

type SetupPendingPageViewProps = UseSetupPendingPageResult;

// The mark is a logo, not a heading: a `span`, so the page's only heading is
// its title.
const asSpan: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <span {...domProps}>{children}</span>;

// The title is the page's `h1`, sized as a card title rather than a page
// banner: the card is meant to read as calm.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/**
 * A calm card on the full-screen background: the organisation, what is
 * happening and who to ask, who is signed in, the language switch and Sign
 * out. Nothing links into the shell: there is nothing to open until the
 * owner finishes onboarding.
 */
export const SetupPendingPageView: React.FC<SetupPendingPageViewProps> = ({
    organisationName,
    userName,
    roleLabel,
    isSigningOut,
    onSignOut,
}) => {
    const { t } = useTranslation('pages');

    return (
        <main
            className="flex min-h-screen w-full items-center justify-center bg-background px-4 py-10"
            {...getNodeTestIdProp('setup-pending-page')}
        >
            <Card
                className="w-full max-w-[480px]"
                {...getNodeTestIdProp('setup-pending-card')}
            >
                <Card.Header className="flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-3">
                        <Typography
                            render={asSpan}
                            type="h5"
                            weight="bold"
                            {...getNodeTestIdProp('setup-pending-mark')}
                        >
                            BOS
                        </Typography>
                        <DashboardLanguageToggle
                            id="setup-pending-language"
                            itemIdPrefix="setup-pending"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Typography
                            color="muted"
                            type="body-sm"
                            {...getNodeTestIdProp('setup-pending-organisation')}
                        >
                            {organisationName}
                        </Typography>
                        <Typography
                            render={asH1}
                            type="h3"
                            {...getNodeTestIdProp('setup-pending-title')}
                        >
                            {t('onboarding.setupPending.title')}
                        </Typography>
                    </div>
                </Card.Header>
                <Card.Content className="flex flex-col gap-3">
                    <Typography.Paragraph
                        {...getNodeTestIdProp('setup-pending-explanation')}
                    >
                        {t('onboarding.setupPending.explanation', {
                            organisation: organisationName,
                        })}
                    </Typography.Paragraph>
                    <Typography
                        color="muted"
                        type="body-sm"
                        {...getNodeTestIdProp('setup-pending-signed-in')}
                    >
                        {t('onboarding.setupPending.signedInAs', {
                            name: userName,
                            role: roleLabel,
                        })}
                    </Typography>
                </Card.Content>
                <Card.Footer>
                    <Button
                        className="w-full"
                        isPending={isSigningOut}
                        onPress={onSignOut}
                        variant="secondary"
                        {...getNodeTestIdProp('setup-pending-sign-out')}
                    >
                        {t('onboarding.setupPending.signOut')}
                    </Button>
                </Card.Footer>
            </Card>
        </main>
    );
};
